import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';

import { useProduct } from '@/features/products/hooks/use-product';
import { useDeleteProduct } from '@/features/products/hooks/use-delete-product';
import { useUpdateProductStatus } from '@/features/products/hooks/use-update-product';
import { AssociateProductsManager } from '@/features/products/containers/associate-products-manager';
import {
  ProductDetailHeader,
  ProductImageGallery,
  ProductPricingDisplay,
  ProductInventoryDisplay,
  ProductPromotionDisplay,
  ProductDetailsDisplay,
  ProductMetricsDisplay,
  ProductAssociationsSection,
} from '@/features/products/components';

import { formatPrice, formatDate, getAmountFromPriceObject, parseStringArray } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCategoryTranslation } from '@/hooks/use-category-translation';

export default function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading, error, isFetching } = useProduct(id ?? '');
  const deleteProduct = useDeleteProduct();
  const updateProductStatus = useUpdateProductStatus();
  const { currency } = useConfigStore();
  const { translateCategory } = useCategoryTranslation();

  const [isAssociateManagerOpen, setIsAssociateManagerOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleDelete = () => {
    if (!product) return;
    deleteProduct.mutate(
      { id: product.refId || product.id },
      {
        onSuccess: () => {
          toast.success(t('products.deleteSuccess', 'Produit supprimé avec succès'));
          navigate('/products');
        },
        onError: () => {
          toast.error(t('products.deleteError', 'Erreur lors de la suppression du produit'));
        },
      }
    );
  };

  const handleToggleStatus = () => {
    if (!product) return;
    const isActive = product.status === 'active';
    const newStatus = isActive ? 'disabled' : 'active';
    updateProductStatus.mutate(
      { productId: product.refId || product.id, status: newStatus },
      {
        onSuccess: () => {
          toast.success(
            newStatus === 'active'
              ? t('products.activateSuccess', 'Produit activé avec succès')
              : t('products.deactivateSuccess', 'Produit désactivé avec succès')
          );
        },
        onError: () => {
          toast.error(t('products.statusUpdateError', 'Erreur lors de la mise à jour du statut'));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="aspect-square rounded-lg" />
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('products.error')}</p>
      </div>
    );
  }

  const productRefId = product.refId || product.id;

  // Parse tags and sizes
  const tagsArray = parseStringArray(product.tags as string | string[] | undefined);
  const sizesArray = parseStringArray(product.sizes as string | string[] | undefined);

  // Handle promotion
  const promotion = product.promotion as {
    refId?: string;
    name?: string;
    type?: string;
    value?: number;
    status?: string;
    badgeColor?: string;
    buyQuantity?: number;
    getQuantity?: number;
  } | null | undefined;

  const price = getAmountFromPriceObject(product.prices, currency, product.price);

  return (
    <div className="space-y-4 md:space-y-6 w-full overflow-x-hidden">
      <ProductDetailHeader
        product={product}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
        isUpdatingStatus={updateProductStatus.isPending || isFetching}
        isDeleting={deleteProduct.isPending}
        translateCategory={translateCategory}
      />

      <div className="grid gap-4 md:gap-6 lg:grid-cols-2 w-full">
        <ProductImageGallery
          images={product.images || []}
          productName={product.name}
          selectedIndex={selectedImageIndex}
          lightboxOpen={lightboxOpen}
          onOpenLightbox={(index) => {
            setSelectedImageIndex(index);
            setLightboxOpen(true);
          }}
          onCloseLightbox={() => setLightboxOpen(false)}
          onChangeIndex={setSelectedImageIndex}
        />

        <div className="space-y-6 min-w-0">
          <ProductPricingDisplay
            price={price}
            currency={currency}
            prices={product.prices}
            comparePrice={product.comparePrice}
            discount={product.discount}
          />

          <ProductInventoryDisplay
            stock={product.stock}
            sku={product.sku}
            unit={product.unit}
          />

          {promotion && (
            <ProductPromotionDisplay
              promotion={promotion}
              price={price}
              currency={currency}
            />
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <ProductDetailsDisplay
          gender={product.gender}
          weight={product.weight}
          weightUnit={product.weight_unit}
          sizes={sizesArray}
          tags={tagsArray}
        />

        <ProductMetricsDisplay
          metrics={product.metrics}
          reviews={product.reviews}
          likedCount={product.likedCount}
        />
      </div>

      <ProductAssociationsSection
        associations={product.associations}
        group={product.group}
        productRefId={productRefId}
        onManageClick={() => setIsAssociateManagerOpen(true)}
      />

      {/* Description */}
      {product.description && (
        <Card>
          <CardHeader>
            <CardTitle>{t('products.description')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{product.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Variants */}
      {Array.isArray(product.variants) && product.variants.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('products.variants')} ({product.variants.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {product.variants.map((variant, index) => (
                <div key={variant.id || index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{variant.name}</p>
                    {variant.sku && <p className="text-xs text-muted-foreground">SKU: {variant.sku}</p>}
                    {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                      <div className="flex gap-2 mt-1">
                        {Object.entries(variant.attributes).map(([key, value]) => (
                          <Badge key={key} variant="outline" className="text-xs">
                            {key}: {value}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(variant.price, currency)}</p>
                    <Badge variant={variant.stock > 0 ? 'default' : 'destructive'} className="text-xs">
                      {variant.stock} {t('products.inStock')}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attributes */}
      {product.attributes && Object.keys(product.attributes).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('products.attributes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(product.attributes).map(([key, value]) => (
                <div key={key} className="flex justify-between p-2 rounded bg-muted">
                  <span className="text-muted-foreground capitalize">{key}</span>
                  <span className="font-medium">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timestamps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            {(product.created_at || product.createdAt) && (
              <div>
                <span>{t('common.createdAt')}: </span>
                <span className="text-foreground">{formatDate(product.created_at || product.createdAt)}</span>
              </div>
            )}
            {(product.updated_at || product.updatedAt) && (
              <div>
                <span>{t('common.updatedAt')}: </span>
                <span className="text-foreground">{formatDate(product.updated_at || product.updatedAt)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <AssociateProductsManager
        open={isAssociateManagerOpen}
        onOpenChange={setIsAssociateManagerOpen}
        currentProduct={product}
        shopId={product.shop?.id || product.shopId || ''}
      />
    </div>
  );
}
